import { last } from 'lodash';

import { resolve, strokeSegment, dot } from 'gear';

import { Bounds, Curve, Cubic, Segment,
         Shape, Polyregion, Region, AbsoluteAnchor } from 'cdl';

import { Tool, usingTouch, padScaling } from '../view';

const redraw = function() {
  this.view.redraw('tools');
};

export const DrawTool = Tool.extend({ typeName: 'DrawTool',

  Properties: {
    pen   : { type: 'Pen'  , def: Tool.defaultPen()  , onChange: redraw },
    brush : { type: 'Brush', def: Tool.defaultBrush(), onChange: redraw }
  },

  target: 'document',

  type: 'Final',
  toolbarName: 'drawTool',

  initTool( view, config = {} ) {
    this._closedConfig = config.closed !== undefined ? config.closed : false;
    this._flushShape();
    this._flushPath();
    this.closeDistance   = 0.1  * 96;
    this.editingDistance = 0.05 * 96;
  },

  _flushShape() {
    this._paths = [];
    this._closed = this._closedConfig;
  },

  _flushPath() {
    this.toolhelp('Click to begin path',
                  'Enter or Double Click to commit, Esc to cancel');
    this._nextPiece = null;
    this._pieces = [];
    this._redoPieces = [];
    this._editingPiece = null;
    this._initialPoint = null;
  },

  on_nextpiece() {
    this.toolhelp('Click to add new point',
                  'Click and hold to add a curve',
                  'Click on the initial point to close',
                  'Double Click to leave open');
  },

  draw({ view, context, transform }) {

    // Draw semitransparent mask
    // context.fillStyle = 'rgba(255,255,255,0.75)';
    // view.fill(context);

    const pieces = this._pieces.slice();
    const pen = this._pen, brush = this._brush;

    let lastWasClosed = false;

    const drawPath = function(closed) {
      pen.setupContext(context,transform);
      context.stroke();

      if (closed) {
        if (brush._type === 'Brush') {
          context.fillStyle = brush._color.rgba;
        } else {
          // Draw temporary semitransparent mask
          // Brush is applied to the Polyregion in commitChanges$()
          // This way all brush types are supported
          context.fillStyle = 'rgba(255,255,255,0.5)';
        }

        context.fill('evenodd');
      }

      context.beginPath();
    };

    context.beginPath();
    let paths = 0;

    for(let k=0,kEnd=this._paths.length;k<kEnd;++k) {
      const paths_k = this._paths[k];
      const { closed } = paths_k;

      if( closed !== lastWasClosed && paths > 0 ) {
        drawPath(lastWasClosed);
        paths = 0;
      }

      paths_k.draw(context,transform);
      paths++;
      lastWasClosed = closed;
    }

    const dotSize = view.viewDeltaToTexture(3);

    if( this._drawing ) {

      const closed = this._closed || this._pathIsClosed();

      if( closed !== lastWasClosed && paths > 0 ) {
        drawPath(lastWasClosed);
        paths = 0;
      }

      const ep = this._editingPiece;
      if( ep ) {
        pieces.push( ep );
      }
      else if( this._nextPiece && ! usingTouch ) {
        pieces.push( this._nextPiece );
      }
      if( pieces.length > 0 ) {
        const curve = new Curve(pieces,closed); // Always false to show feedback on the missing part
        curve.draw(context,transform);
        paths++;

        drawPath(closed);
        paths = 0;

        context.lineWidth = view.viewDeltaToTexture(1);
        context.strokeStyle = 'rgba(10,150,50,1.0)';

        if( ep && ep instanceof Cubic ) {
          context.beginPath();
          const c1 = ep.P2, c2 = ep.P3 .plus( ep.P3.minus(ep.P2) );
          strokeSegment(context,c1,c2,transform);
          context.fillStyle = 'rgba(10,100,30,1.0)';
          dot(context,c1,dotSize,transform);
          dot(context,c2,dotSize,transform);
        }
        else if( this._lastPieceWasModified && this._pieces.length > 0 ) {
          const piece = last(this._pieces);
          if( piece instanceof Cubic ) {
            context.beginPath();
            const c1 = piece.P2, c2 = piece.P3 .plus( piece.P3.minus(piece.P2) );
            strokeSegment(context,c1,c2,transform);
            context.stroke();
          }
        }
      }
    }

    if( paths > 0 ) {
      drawPath(lastWasClosed);
    }

    if( this._drawing ) {
      const l = this._nextPiece ? this._nextPiece.P0 : ( this._pieces.length > 0 ? last(this._pieces).last() : null );
      if( l ) {
        // Draw a dot in the last commited point, because we do not have pointer move feedback in touch
        context.beginPath();
        context.fillStyle = 'rgba(240,60,40,1.0)';
        dot(context,l,dotSize,transform);
      }
    }
  },

  on_pointerdown({ event, isPrimary, which, docPoint, viewPoint }) {

    if( ! isPrimary ) {
      return; // ignore multitouch
    }

    const leftClick = which === 1 ;
    if( leftClick ) {

      event.stopPropagation();

      this._downPos = viewPoint;

      this._drawing = true;
      this.on_nextpiece();

      this._redoNextPiece = null;
      this._redoPieces = [];
      const np = docPoint;
      this._currentPoint = np;

      const nextPiece = this._nextPiece;
      if( nextPiece ) {
        if( nextPiece.P2 ) {
          nextPiece.P2 = np.clone();
        }
        nextPiece.last(np.clone());
      }

      if( this._pushPathIfClosed() ) {
        return;
      }

      if( ! this._initialPoint ) {
        this._initialPoint = np.clone();
      }

      this._editingPiece = this._nextPiece;

      this._nextPiece = new Segment(np,np);

      if( this._pieces.length > 0 && np.isEqual(last(this._pieces).P0) ) {
        this._nextPiece = null;
        this._pushPath(this._closed);
      }

      this.redraw();
    }
    const rightClick = event.which === 3;
    if( rightClick ) {
      event.stopPropagation();
      this.commit$();
    }
  },

  on_mousemove(event) {
    this.on_pointermove(event);
    event.stopPropagation();
  },

  on_pointermove({ event, view, isPrimary }) {
    if( ! isPrimary ) {
      return; // ignore multitouch
    }
    if( ! this._drawing ) {
      return;
    }

    const editingDistance = this.editingDistance * padScaling;

    const np = event.docPoint;
    this._currentPoint = np;

    let pieces = this._pieces, nextPiece = this._nextPiece;

    if( ! nextPiece && pieces.length > 0 ) {
      const P0 = last(pieces).last();
      nextPiece = this._nextPiece =
        this._lastPieceWasModified ? new Cubic(P0,np,np,np)
                                   : new Segment(P0,np) ;
      this._redoNextPiece = null;
    }

    let editingPiece = this._editingPiece;

    if( editingPiece && ! this._downPos.isNear( event.viewPoint, editingDistance ) ) {
      if( editingPiece instanceof Segment ) {
        this.toolhelp('Hold and move to change control point');
        editingPiece = this._editingPiece = Cubic.fromSegment(editingPiece.P0,editingPiece.P1);
        editingPiece.P1 = editingPiece.P0.clone();
      }
      editingPiece.P2 = editingPiece.P3 .plus( editingPiece.P3.minus(np) );
      this._editingPieceModified = true;
    }
    else if( nextPiece ) {
      if( nextPiece.P2 ) {
        nextPiece.P2 = np.clone();
      }
      nextPiece.last(np.clone());
    }

    this.redraw();
  },

  on_pointerup({ event, isPrimary }) {
    if( ! isPrimary ) {
      return; // ignore multitouch
    }
    if( ! this._drawing ) {
      return;
    }


    const editingDistance = this.editingDistance * padScaling;

    const editingPiece = this._editingPiece, pieces = this._pieces;

    if( editingPiece && ! editingPiece.P0.isNear(editingPiece.last(),editingDistance) ) {

      pieces.push( editingPiece );

      if( this._editingPieceModified ) {
        const lastPiece = last(pieces);
        const P0 = lastPiece.P3;
        const PC = P0 .plus( P0.minus(lastPiece.P2) );
        this._nextPiece = new Cubic(P0,PC.clone(),PC.clone(),PC.clone());
        this.on_nextpiece();
      }
    }
    this._editingPiece = null;
    this._lastPieceWasModified = this._editingPieceModified;
    this._editingPieceModified = false;
    this._downPos = null;
    this.redraw();
  },

  on_dblclick() {
    this.commit$();
  },

  undo() {
    if( ! this._drawing ) {
      if( this._paths.length > 0 ) {
        this._redoPaths.push(this._paths.pop());
      }
      this.view.redraw('tools');
    }
    else {
      if( this._nextPiece ) {
        this._redoNextPiece = this._nextPiece;
        this._nextPiece = null;
      }
      else if( this._pieces.length > 0 ) {
        this._redoPieces.push( this._pieces.pop() );
      }
      this.view.redraw('tools');
    }
  },

  redo() {
    if( ! this._drawing ) {
       if( this._redoPaths.length > 0 ) {
        this._paths.push( this._redoPaths.pop() );
      }
      this.view.redraw('tools');
    }
    else {
      if( this._redoPieces.length > 0 ) {
        const rp = this._redoPieces.pop();
        this._pieces.push(rp);
        this._nextPiece = null;
      }
      else if( this._redoNextPiece ) {
        this._nextPiece = this._redoNextPiece;
        this._redotNextPiece = null;
      }
      this.view.redraw('tools');
    }
  },

  _pathIsClosed() {
    const { view } = this;

    const ip = this._initialPoint, cp = this._currentPoint;
    return ip && cp &&
           this._pieces.length > 0 &&
           ip.isNear( cp, view.viewDeltaToDoc(this.closeDistance) * padScaling );
  },

  _pushPathIfClosed() {
    if( this._pathIsClosed() ) {
      if( this._nextPiece ) {
        this._nextPiece.last(this._initialPoint);
        this._pieces.push(this._nextPiece);
        this._nextPiece = this._editingPiece = null;
      }
      const closed = true;
      this._pushPath(closed);
      return true;
    }
    return false;
  },

  _pushPath( closed) {
    if( closed === undefined ) {
      if( this._pushPathIfClosed() ) {
        return resolve();
      }
      else {
        closed = false;
      }
    }

    const pieces = this._pieces;

    const nextPiece = this._nextPiece;
    if( nextPiece && !nextPiece.P0.isEqual(nextPiece.last()) ) {
      pieces.push(nextPiece.clone());
    }

    this._pieces = [];
    if( pieces.length >= 1 ) {
      if( closed && !pieces[0].P0.isEqual(last(pieces).last())) {
        pieces.push(new Segment(nextPiece.last(),pieces[0].P0));
      }

      const curve = Curve.fromNonTiedPieces(pieces,closed);
      this._paths.push( curve );
      this.view.redraw('tools');
    }

    this._flushPath();

    // Force a commit after each path
    return this.commitChanges$();
  },

  _cancel$() {
    // Nothing to do...
  },

  commit$() {
    if( this._drawing && this._pieces.length > 0 ) {
      return this._pushPath(this._closed);
    }
    else {
      return this.callParent();
    }
  },

  _commit$() {
    this.commitChanges$();
  },

  commitChanges$() {
    const view = this.view;
    if( this._paths.length === 0 ) {
      return;
    }
    const pen = this._pen, brush = this._brush;

    const polyregions = [];
    const pushPolyregion = function( regions, closed ) {
      const polyregion = new Polyregion({ regions, pen, fillMode: 'Alternate' });
      if( closed ) {
        polyregion.brush = brush;
      }
      polyregions.push(polyregion);
    };

    let regions = [];
    let lastWasClosed = false;

    for(let k=0,kEnd=this._paths.length;k<kEnd;++k) {
      const paths_k = this._paths[k];
      const closed = paths_k.closed;
      if( closed !== lastWasClosed && regions.length > 0 ) {
        pushPolyregion(regions,lastWasClosed);
        regions = [];
      }
      const region = new Region([paths_k]);
      regions.push(region);
      lastWasClosed = closed;
    }

    if( regions.length > 0 ) {
      pushPolyregion(regions,lastWasClosed);
    }
    const anchor = Bounds.ofThings(polyregions).center;
    view.add( Shape({ polyregions:polyregions,
                      anchor: AbsoluteAnchor(anchor),
                      pin: anchor.clone() }) );

    this._flushPath();
    this._flushShape();
    view.keepSelection();
    return view.commit$();
  }
});
Tool.Draw = DrawTool;

export const DrawShapeTool = DrawTool.extend({ typeName: 'DrawShapeTool',
  init( config = {} ) {
    config.closed = true;
    this.callParent(config);
  }
});
Tool.DrawShape = DrawShapeTool;



/** WEBPACK FOOTER **
 ** ./src/ui/tool/draw.js
 **/